import React, { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { TrashIcon } from '@heroicons/react/16/solid';
import { Button } from '../common/Catalyst/button';
import {
  AmenityClosureReturn,
  AmenityReturn,
  ReservationReturn,
} from '../clients/rust-api-client';
import { amenityService } from '../clients/ApiClients';
import {
  conflictsFrom,
  formatLocalRange,
  fromDateTimeInput,
  reservationErrorMessage,
} from '../reservation/reservationMeta';
import { helpClass, inputClass, labelClass } from '../reservation/formStyles';
import { ErrorAlert } from '../common/Custom/ErrorAlert';
import { ConflictDialog } from './ConflictDialog';

/**
 * Closures (#642): times the amenity cannot be booked — maintenance, a
 * holiday, the association's own event. The reason is shown to
 * households. A closure over existing bookings is refused (409) until the
 * manager confirms cancelling them, with a reason, in the conflict dialog.
 */
interface Props {
  amenity: AmenityReturn;
  readOnly: boolean;
  onChanged: () => void;
}

export const AmenityClosures: React.FC<Props> = ({ amenity, readOnly, onChanged }) => {
  const [closures, setClosures] = useState<AmenityClosureReturn[]>([]);
  const [starts, setStarts] = useState('');
  const [ends, setEnds] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflicts, setConflicts] = useState<ReservationReturn[] | null>(null);
  const [conflictError, setConflictError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const list = (await amenityService.listAmenityClosures({ amenityId: amenity.id })).data.closures;
      setClosures([...list].sort((a, b) => b.starts_local.localeCompare(a.starts_local)));
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't load closures."));
    }
  }, [amenity.id]);

  useEffect(() => {
    load();
  }, [load]);

  const startsLocal = fromDateTimeInput(starts);
  const endsLocal = fromDateTimeInput(ends);
  const valid = !!startsLocal && !!endsLocal && endsLocal > startsLocal && reason.trim().length > 0;

  const create = async (cancelReason?: string) => {
    if (!startsLocal || !endsLocal) return;
    await amenityService.createAmenityClosure({
      amenityId: amenity.id,
      amenityClosureCreate: {
        starts_local: startsLocal,
        ends_local: endsLocal,
        reason: reason.trim(),
        cancel_conflicting: cancelReason ? true : null,
        cancel_reason: cancelReason ?? null,
      },
    });
    setStarts('');
    setEnds('');
    setReason('');
    setConflicts(null);
    await load();
    onChanged();
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    setSaving(true);
    setError(null);
    try {
      await create();
    } catch (err) {
      const inTheWay = conflictsFrom(err);
      if (inTheWay) {
        setConflictError(null);
        setConflicts(inTheWay);
      } else {
        setError(reservationErrorMessage(err, "Couldn't add the closure."));
      }
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c: AmenityClosureReturn) => {
    if (!window.confirm(`Remove the closure “${c.reason}”? The time becomes bookable again.`)) return;
    try {
      await amenityService.deleteAmenityClosure({ amenityId: amenity.id, closureId: c.id });
      await load();
      onChanged();
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't remove the closure."));
    }
  };

  const thisYear = new Date().getFullYear();

  return (
    <div className="max-w-2xl space-y-4">
      <p className={helpClass}>Times in {amenity.timezone}. The reason is shown to households.</p>

      {!readOnly && (
        <form onSubmit={submit} className="space-y-3 rounded-lg border border-line p-4">
          <div className="flex flex-wrap gap-3">
            <label className={labelClass}>
              From
              <input type="datetime-local" value={starts} onChange={(e) => setStarts(e.target.value)} className={inputClass} />
            </label>
            <label className={labelClass}>
              Until
              <input type="datetime-local" value={ends} onChange={(e) => setEnds(e.target.value)} className={inputClass} />
            </label>
          </div>
          <label className={labelClass}>
            Reason
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Pool resurfacing"
              className={inputClass}
            />
          </label>
          {startsLocal && endsLocal && endsLocal <= startsLocal && (
            <ErrorAlert>The closure must end after it starts.</ErrorAlert>
          )}
          <Button color="sky" type="submit" disabled={!valid || saving}>
            {saving ? 'Adding…' : 'Add closure'}
          </Button>
        </form>
      )}

      <ErrorAlert>{error}</ErrorAlert>

      {closures.length === 0 ? (
        <p className="text-[13px] text-fg-muted">No closures.</p>
      ) : (
        <ul className="divide-y divide-line/60 rounded-lg border border-line">
          {closures.map((c) => (
            <li key={c.id} className="flex items-center gap-3 px-3 py-2 text-[13px]">
              <span className="min-w-0 flex-1">
                <span className="block font-medium text-fg">{c.reason}</span>
                <span className="block text-[12px] text-fg-muted">
                  {formatLocalRange(c.starts_local, c.ends_local, thisYear)}
                </span>
              </span>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => remove(c)}
                  aria-label={`Remove closure: ${c.reason}`}
                  className={clsx('rounded p-1 text-fg-muted hover:bg-fg/5 hover:text-fg')}
                >
                  <TrashIcon className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <ConflictDialog
        open={conflicts !== null}
        action={`close ${amenity.name}`}
        conflicts={conflicts ?? []}
        error={conflictError}
        onClose={() => setConflicts(null)}
        onConfirm={async (cancelReason) => {
          try {
            await create(cancelReason);
          } catch (err) {
            setConflictError(reservationErrorMessage(err, "Couldn't add the closure."));
          }
        }}
      />
    </div>
  );
};

export default AmenityClosures;
